import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Eye } from "lucide-react";
import { cn } from "../../lib/utils";
import type { MmObject } from "../../api/mentalModels";
import { mmTypeName } from "./MmRow";
import { filterByType, groupByArea, needsALook, presentTypes } from "../../lib/mmListSections";

type Tm = (key: string, fallback: string) => string;

interface Props {
  objects: MmObject[];
  spaces: Array<{ _id: string; name: string }>;
  tm: Tm;
  renderRow: (o: MmObject) => React.ReactNode;
  onOpen: (o: MmObject) => void;
}

/**
 * Studio list (spec 2026-11-03 §C): type filter, a "Needs a look" strip for
 * long lists, then sections by area with "Not in an area yet" last.
 */
const MmListSections: React.FC<Props> = ({ objects, spaces, tm, renderRow, onOpen }) => {
  const { t } = useTranslation();
  const [type, setType] = useState<string | null>(null);
  const types = useMemo(() => presentTypes(objects), [objects]);
  const activeType = type && types.includes(type) ? type : null;
  const visible = useMemo(() => filterByType(objects, activeType), [objects, activeType]);
  const strip = useMemo(() => needsALook(visible), [visible]);
  const sections = useMemo(() => groupByArea(visible, spaces), [visible, spaces]);

  const chip = (selected: boolean) =>
    cn(
      "rounded-full border px-3 py-1 text-xs transition-colors",
      selected
        ? "border-primary/40 bg-primary/10 text-primary"
        : "border-border text-muted-foreground hover:border-primary/30 hover:text-foreground"
    );

  return (
    <div className="space-y-5">
      {types.length > 1 && (
        <div role="group" aria-label={tm("filter_type", "Filter by type")} className="flex flex-wrap gap-1.5">
          <button type="button" aria-pressed={!activeType} onClick={() => setType(null)} className={chip(!activeType)}>
            {tm("filter_all", "All")}
          </button>
          {types.map((ty) => (
            <button
              key={ty}
              type="button"
              aria-pressed={activeType === ty}
              onClick={() => setType(activeType === ty ? null : ty)}
              className={chip(activeType === ty)}
            >
              {mmTypeName(t, ty)}
            </button>
          ))}
        </div>
      )}

      {strip.length > 0 && (
        <section aria-label={tm("needs_a_look", "Needs a look")} className="rounded-2xl border border-primary/15 bg-primary/[0.03] p-3">
          <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-primary/70 mb-2">
            <Eye className="w-3.5 h-3.5" />
            {tm("needs_a_look", "Needs a look")}
          </p>
          <ul className="space-y-1">
            {strip.map((o) => (
              <li key={o._id}>
                <button
                  type="button"
                  onClick={() => onOpen(o)}
                  className="w-full text-start rounded-lg px-2 py-1.5 text-sm truncate hover:bg-primary/5"
                >
                  {o.label || o.workingStatement || o.statement}
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      {sections.map((s) => (
        <section key={s.spaceId ?? "__unfiled"} aria-label={s.name ?? tm("area_none", "Not in an area yet")}>
          <h3 className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/70 mb-2">
            {s.name ?? tm("area_none", "Not in an area yet")}
            <span className="ms-1.5 font-normal text-muted-foreground/50">({s.objects.length})</span>
          </h3>
          <div className="space-y-2">
            {s.objects.map((o) => (
              <React.Fragment key={o._id}>{renderRow(o)}</React.Fragment>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
};

export default MmListSections;
